import React, { useCallback, useEffect, useState } from 'react';
import { useStyles } from 'components/Statistics/styles';

import clsx from 'clsx';

import SearchButton from 'components/Button/SearchButton';
import Partial from './Partial';
import Download from './Download';
import Storage from './Strorage';

import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';

import KoLocalizedUtils from 'commons/utils/DateUtils';
import koLocale from 'date-fns/locale/ko';
import Asset from './Asset';
import dayjs from 'dayjs';
import useStatistics from 'hooks/useStatistics';
import StatisticsParamVO, { DownloadType, TaskType } from 'commons/vo/param/StatisticsParamVO';
import { RootState } from 'redux/reducers/type';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { makeLineChartData, makePieChartAssetData, makePieChartStorageData } from './utils';
import Streaming from './Streaming';
import Login from './Login';

export const CHART_WIDTH = 900;
export const CHART_HEIGHT = 350;
export const PIE_CHART_OUTER_RADIUS = CHART_HEIGHT / 2 - 50;
export const INNER_PIE_CHART_OUTER_RADIUS = PIE_CHART_OUTER_RADIUS - 10;
export const OUTER_PIE_CHART_INNER_RADIUS = PIE_CHART_OUTER_RADIUS - 6;
export const INNER_PIE_CHART_INNER_RADIUS = PIE_CHART_OUTER_RADIUS / 2 - 10;
// export const CHART_COLORS = ['#345feb', '#34eb43'];
export const CHART_COLORS = {
  disable: '#adadad',
  default: '#345feb',
  caution: '#ebae34',
  warning: '#eb4c34',
};
export const RADIAN = Math.PI / 180;

const TODAY = new Date();

// 달력의 최소, 최대 월
const MIN_YEAR = 2016;
const MIN_MONTH = 11;
const MIN_DATE = new Date(MIN_YEAR, MIN_MONTH - 1);

function Statistics() {
  // hooks
  const classes = useStyles();
  const {
    onRequestStatisticsAsset,
    onRequestStatisticsPartial,
    onRequestStatisticsDownload,
    onRequestStatisticsStreaming,
    onRequestStatisticsLogin,
    onRequestStatisticsStorage,
  } = useStatistics();

  // 시작 월, 종료 월 기본값
  const isFirstDay = TODAY.getDate() === 1;
  const defaultDateFrom = isFirstDay
    ? new Date(TODAY.getFullYear(), TODAY.getMonth() - 1, 1)
    : new Date(TODAY.getFullYear(), TODAY.getMonth(), 1);
  const defaultDateTo = new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - 1);

  // data
  const { isLoading: isLoadingAsset, statistics: statisticsAsset } = useSelector(
    (state: RootState) => state.statisticsAsset,
  );
  const { isLoading: isLoadingPartial, statistics: statisticsPartial } = useSelector(
    (state: RootState) => state.statisticsPartial,
  );
  const { isLoading: isLoadingDownload, statistics: statisticsDownload } = useSelector(
    (state: RootState) => state.statisticsDownload,
  );
  const { isLoading: isLoadingStreaming, statistics: statisticsStreaming } = useSelector(
    (state: RootState) => state.statisticsStreaming,
  );
  const { isLoading: isLoadingLogin, statistics: statisticsLogin } = useSelector(
    (state: RootState) => state.statisticsLogin,
  );
  const { isLoading: isLoadingStorage, statistics: statisticsStorage } = useSelector(
    (state: RootState) => state.statisticsStorage,
  );

  // state
  const [dateFrom, setDateFrom] = useState<Date>(defaultDateFrom);
  const [dateTo, setDateTo] = useState<Date>(defaultDateTo);

  const [downloadType, setDownloadType] = useState<DownloadType>(TaskType.Download);
  const [currentStorageIndex, setCurrentStorageIndex] = useState<number>(0);
  // state - 차트 데이터
  const [statisticsAssetData, setStatisticsAssetData] = useState<PieChartAssetData[]>([]);
  const [totalAsset, setTotalAsset] = useState<PieTotalAssetData>({
    count: 0,
    fileSize: 0,
  });
  const [statisticsPartialData, setStatisticsPartialData] = useState<LineChartData[]>([]);
  const [statisticsDownloadData, setStatisticsDownloadData] = useState<LineChartData[]>([]);
  const [statisticsStreamingData, setStatisticsStreamingData] = useState<LineChartData[]>([]);
  const [statisticsLoginData, setStatisticsLoginData] = useState<LineChartData[]>([]);
  const [statisticsStorageData, setStatisticsStorageData] = useState<PieChartStorageData[]>([]);
  const [totalStorage, setTotalStorage] = useState<PieTotalStorageData>({
    total: 0,
  });

  const startDate = dayjs(dateFrom).format('YYYYMMDD');
  const endDate = dayjs(dateTo).format('YYYYMMDD');

  // 에셋 통계
  useEffect(() => {
    const param = new StatisticsParamVO({
      startDate,
      endDate,
    });

    onRequestStatisticsAsset(param);
  }, [onRequestStatisticsAsset]);

  useEffect(() => {
    makePieChartAssetData(isLoadingAsset, statisticsAsset, setStatisticsAssetData, setTotalAsset);
  }, [isLoadingAsset]);

  // 부분인출
  useEffect(() => {
    const param = new StatisticsParamVO({
      taskType: TaskType.Partial,
      startDate,
      endDate,
    });

    onRequestStatisticsPartial(param);
  }, [onRequestStatisticsPartial]);

  useEffect(() => {
    makeLineChartData(
      isLoadingPartial,
      statisticsPartial,
      dateFrom,
      dateTo,
      setStatisticsPartialData,
    );
  }, [isLoadingPartial]);

  // 다운로드
  useEffect(() => {
    const param = new StatisticsParamVO({
      taskType: downloadType,
      startDate,
      endDate,
    });

    onRequestStatisticsDownload(param);
  }, [onRequestStatisticsDownload, downloadType]);

  useEffect(() => {
    makeLineChartData(
      isLoadingDownload,
      statisticsDownload,
      dateFrom,
      dateTo,
      setStatisticsDownloadData,
    );
  }, [isLoadingDownload]);

  // 스트리밍
  useEffect(() => {
    const param = new StatisticsParamVO({
      taskType: TaskType.Streaming,
      startDate,
      endDate,
    });

    onRequestStatisticsStreaming(param);
  }, [onRequestStatisticsStreaming]);

  useEffect(() => {
    makeLineChartData(
      isLoadingStreaming,
      statisticsStreaming,
      dateFrom,
      dateTo,
      setStatisticsStreamingData,
    );
  }, [isLoadingStreaming]);

  // 로그인
  useEffect(() => {
    const param = new StatisticsParamVO({
      taskType: TaskType.Login,
      startDate,
      endDate,
    });

    onRequestStatisticsLogin(param);
  }, [onRequestStatisticsLogin]);

  useEffect(() => {
    makeLineChartData(isLoadingLogin, statisticsLogin, dateFrom, dateTo, setStatisticsLoginData);
  }, [isLoadingLogin]);

  // 스토리지
  useEffect(() => {
    onRequestStatisticsStorage();
  }, [onRequestStatisticsStorage]);

  useEffect(() => {
    makePieChartStorageData(
      isLoadingStorage,
      statisticsStorage,
      currentStorageIndex,
      setStatisticsStorageData,
      setTotalStorage,
    );
  }, [isLoadingStorage, currentStorageIndex]);

  // 날짜 검색 관련 함수
  const onClickSearch = useCallback(() => {
    onRequestStatisticsAsset(
      new StatisticsParamVO({
        startDate,
        endDate,
      }),
    );
    onRequestStatisticsPartial(
      new StatisticsParamVO({
        taskType: TaskType.Partial,
        startDate,
        endDate,
      }),
    );
    onRequestStatisticsDownload(
      new StatisticsParamVO({
        taskType: downloadType,
        startDate,
        endDate,
      }),
    );
    onRequestStatisticsStreaming(
      new StatisticsParamVO({
        taskType: TaskType.Streaming,
        startDate,
        endDate,
      }),
    );
    onRequestStatisticsLogin(
      new StatisticsParamVO({
        taskType: TaskType.Login,
        startDate,
        endDate,
      }),
    );
  }, [dateFrom, dateTo, downloadType]);

  const onChangeDateFrom = useCallback(
    (date: Date | null) => {
      if (date) {
        const firstDayOfMonth = dayjs(date).startOf('month').toDate();
        setDateFrom(firstDayOfMonth);

        if (dateTo && firstDayOfMonth > dateTo) {
          setDateTo(dayjs(firstDayOfMonth).endOf('month').toDate());
        }
      } else {
        setDateFrom(null);
      }
    },
    [dateTo],
  );

  const onChangeDateTo = useCallback(
    (date: Date | null) => {
      if (date) {
        const currentDate = dayjs();
        const lastDayOfMonth = dayjs(date).endOf('month').toDate();

        // 설정된 날짜가 오늘인 경우
        if (dayjs(date).isSame(currentDate, 'month')) {
          // 종료날짜를 설정된 어제날짜로 설정
          const yesterday = currentDate.subtract(1, 'day').toDate();
          setDateTo(yesterday);

          // 시작날짜가 설정된 어제날짜보다 늦는 경우
          if (dateFrom && yesterday < dateFrom) {
            // 시작날짜를 설정된 어제날짜로 설정
            setDateFrom(dayjs(yesterday).startOf('month').toDate());
          }
        } else {
          // 종료날짜를 입력된 달의 마지막 날짜로 설정
          setDateTo(lastDayOfMonth);

          // 시작날짜가 입력된 달의 마지막 날짜보다 늦는 경우
          if (dateFrom && lastDayOfMonth < dateFrom) {
            // 시작날짜를 입력된 달의 마지막 날짜로 설정
            setDateFrom(dayjs(lastDayOfMonth).startOf('month').toDate());
          }
        }
      } else {
        setDateTo(null);
      }
    },
    [dateFrom],
  );

  const onChangeDownloadType = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value as DownloadType;
      setDownloadType(value);

      onRequestStatisticsDownload(
        new StatisticsParamVO({
          taskType: value,
          startDate,
          endDate,
        }),
      );
    },
    [downloadType],
  );

  const onChangeCurrentStorage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      setCurrentStorageIndex(parseInt(value));
    },
    [currentStorageIndex],
  );

  return (
    <div className={clsx(classes.container)}>
      <div className={clsx(classes.searchBar)}>
        <div className={clsx(classes.title)}>통계</div>
        <div className={clsx(classes.search)}>
          <MuiPickersUtilsProvider utils={KoLocalizedUtils} locale={koLocale}>
            <DatePicker
              className={clsx(classes.date, 'marginInit')}
              variant="inline"
              inputVariant="outlined"
              format="yyyy-MM"
              margin="normal"
              autoOk
              label="시작 월"
              minDate={MIN_DATE}
              maxDate={TODAY}
              value={dateFrom}
              onChange={() => {}}
              onMonthChange={onChangeDateFrom}
              views={['year', 'month']}
            />
            <DatePicker
              className={clsx(classes.date, 'marginInit')}
              variant="inline"
              inputVariant="outlined"
              format="yyyy-MM"
              margin="normal"
              autoOk
              label="종료 월"
              minDate={MIN_DATE}
              maxDate={TODAY}
              value={dateTo}
              onChange={() => {}}
              onMonthChange={onChangeDateTo}
              views={['year', 'month']}
            />
          </MuiPickersUtilsProvider>

          <div className={clsx(classes.searchButton)}>
            <SearchButton onClick={onClickSearch} />
          </div>
        </div>
      </div>

      <div className={classes.sections}>
        <Asset
          statisticsAssetData={statisticsAssetData}
          totalAsset={totalAsset}
          startDate={startDate}
          endDate={endDate}
        />
        <Partial
          statisticsPartialData={statisticsPartialData}
          startDate={startDate}
          endDate={endDate}
        />
        <Download
          statisticsDownloadData={statisticsDownloadData}
          downloadType={downloadType}
          onChangeDownloadType={onChangeDownloadType}
          startDate={startDate}
          endDate={endDate}
        />
        <Streaming statisticsStreamingData={statisticsStreamingData} />
        <Login statisticsLoginData={statisticsLoginData} startDate={startDate} endDate={endDate} />
        <Storage
          statisticsStorageData={statisticsStorageData}
          totalStorage={totalStorage}
          statisticsStorage={statisticsStorage}
          currentStorageIndex={currentStorageIndex}
          onChangeCurrentStorage={onChangeCurrentStorage}
        />
      </div>
    </div>
  );
}

export default Statistics;
